import { useState, useEffect } from 'react';
import { Mail, Eye, Trash2, CheckCircle, Archive, Filter, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { adminApi } from '@/lib/api';

const AdminSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => { fetchSubmissions(); }, [statusFilter, typeFilter]);

  const fetchSubmissions = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.form_type = typeFilter;
      const res = await adminApi.getSubmissions(params);
      setSubmissions(res.data);
    } catch (error) { toast.error('Failed to fetch submissions'); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try { await adminApi.updateSubmissionStatus(id, status); toast.success('Status updated'); fetchSubmissions(); }
    catch (error) { toast.error('Failed to update status'); }
  };

  const handleDelete = async (id) => {
    try { await adminApi.deleteSubmission(id); toast.success('Deleted'); fetchSubmissions(); }
    catch (error) { toast.error('Failed to delete'); }
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('form_type', typeFilter);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/submissions/export/csv?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'form_submissions.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const getFormTypeLabel = (type) => {
    const labels = {
      'contact': 'Contact Us',
      'consultation': 'Consultation',
      'workshop_request': 'Workshop Request',
      'newsletter': 'Newsletter'
    };
    return labels[type] || type;
  };

  const getFormTypeBadgeColor = (type) => {
    const colors = {
      'contact': 'bg-blue-100 text-blue-800',
      'consultation': 'bg-purple-100 text-purple-800',
      'workshop_request': 'bg-orange-100 text-orange-800',
      'newsletter': 'bg-green-100 text-green-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const statusColors = { new: 'bg-blue-100 text-blue-800', read: 'bg-yellow-100 text-yellow-800', replied: 'bg-green-100 text-green-800', archived: 'bg-gray-100 text-gray-800' };

  return (
    <div className="space-y-6" data-testid="admin-submissions-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-heading font-bold">Form Submissions</h1><p className="text-muted-foreground">Contact forms, workshop requests, and consultation inquiries</p></div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48" data-testid="type-filter">
            <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Form Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Form Types</SelectItem>
            <SelectItem value="contact">Contact Us</SelectItem>
            <SelectItem value="workshop_request">Workshop Requests</SelectItem>
            <SelectItem value="consultation">Consultation</SelectItem>
            <SelectItem value="newsletter">Newsletter</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="read">Read</SelectItem>
            <SelectItem value="replied">Replied</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? <div className="p-8 text-center">Loading...</div> : submissions.length === 0 ? <div className="p-8 text-center text-muted-foreground">No submissions yet</div> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.map((s) => (
                  <TableRow key={s.id} data-testid={`submission-${s.id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell>{s.email}</TableCell>
                    <TableCell><Badge className={getFormTypeBadgeColor(s.form_type)}>{getFormTypeLabel(s.form_type)}</Badge></TableCell>
                    <TableCell><Badge className={statusColors[s.status]}>{s.status}</Badge></TableCell>
                    <TableCell className="text-sm">{formatDate(s.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => { setSelectedSubmission(s); updateStatus(s.id, 'read'); }}><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => updateStatus(s.id, 'replied')}><CheckCircle className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => updateStatus(s.id, 'archived')}><Archive className="w-4 h-4" /></Button>
                        <AlertDialog><AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(s.id)}>Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Submission Details</DialogTitle></DialogHeader>
          {selectedSubmission && (
            <div className="space-y-4">
              <div><strong>Name:</strong> {selectedSubmission.name}</div>
              <div><strong>Email:</strong> <a href={`mailto:${selectedSubmission.email}`} className="text-primary">{selectedSubmission.email}</a></div>
              {selectedSubmission.phone && <div><strong>Phone:</strong> {selectedSubmission.phone}</div>}
              {selectedSubmission.subject && <div><strong>Subject:</strong> {selectedSubmission.subject}</div>}
              <div><strong>Message:</strong><p className="mt-1 p-3 bg-muted rounded">{selectedSubmission.message}</p></div>
              <div className="text-sm text-muted-foreground">Submitted: {formatDate(selectedSubmission.created_at)}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSubmissions;
